import React, { useEffect, useMemo, useState } from "react";
import useApi from "../CustomHooks/useApi";
import LoadingOverlay from "../../LoadingOverlay";
import { getTripBalances } from "../../api/tripBalanceApiClient";
import "../../css/Finance/TripBalanceView.css";

const monthNames = ["styczeń", "luty", "marzec", "kwiecień", "maj", "czerwiec", "lipiec", "sierpień", "wrzesień", "październik", "listopad", "grudzień"];
const formatMoney = (value) => `${Number(value ?? 0).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
const formatDate = (value) => value ? new Date(value).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

const TripBalanceView = ({ refreshTrigger }) => {
    const [year, setYear] = useState(new Date().getFullYear());
    const [rows, setRows] = useState([]);
    const { callApi, isLoading } = useApi();

    useEffect(() => {
        callApi(() => getTripBalances(year), (data) => setRows(data ?? []));
    }, [year, refreshTrigger]);

    const totals = useMemo(() => rows.reduce((acc, row) => ({
        income: acc.income + Number(row.income ?? 0),
        costs: acc.costs + Number(row.costs ?? 0),
        balance: acc.balance + Number(row.balance ?? 0),
        administrativeCosts: acc.administrativeCosts + Number(row.administrativeCosts ?? 0)
    }), { income: 0, costs: 0, balance: 0, administrativeCosts: 0 }), [rows]);

    const years = Array.from({ length: 5 }, (_, index) => new Date().getFullYear() - index);

    return (
        <div className="trip-balance-view">
            {isLoading && <LoadingOverlay text="Ładowanie bilansu wycieczek..." />}

            <div className="trip-balance-toolbar">
                <div>
                    <div className="trip-balance-title">Bilans wycieczek</div>
                    <div className="trip-balance-subtitle">Rzeczywiste przepływy finansowe oraz koszty rozliczeniowe wycieczek.</div>
                </div>
                <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {years.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
            </div>

            {!isLoading && rows.length === 0 ? (
                <div className="trip-balance-empty">Brak wycieczek z przypisanym miesiącem rozliczeniowym dla {year} roku.</div>
            ) : !isLoading && (
                <div className="trip-balance-table-wrap">
                    <table className="trip-balance-table">
                        <thead>
                            <tr>
                                <th>Miesiąc</th>
                                <th>Wycieczka</th>
                                <th className="numeric">Przychody</th>
                                <th className="numeric">Koszty</th>
                                <th className="numeric">Bilans</th>
                                <th className="numeric">%</th>
                                <th className="numeric">Koszty admin.</th>
                                <th>Impreza turystyczna</th>
                                <th>Uwagi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={`${row.tripGuid}-${row.accountingMonth}`}>
                                    <td className="month">{monthNames[new Date(row.accountingMonth).getMonth()]}</td>
                                    <td>
                                        <div className="trip-name">{row.tripName}</div>
                                        <div className="trip-dates">{formatDate(row.startDate)} – {formatDate(row.endDate)}</div>
                                    </td>
                                    <td className="numeric">{formatMoney(row.income)}</td>
                                    <td className="numeric">{formatMoney(row.costs)}</td>
                                    <td className={`numeric balance ${Number(row.balance) < 0 ? "negative" : "positive"}`}>{formatMoney(row.balance)}</td>
                                    <td className={`numeric balance ${Number(row.balance) < 0 ? "negative" : "positive"}`}>{row.balanceToCostsPercent == null ? "—" : `${Number(row.balanceToCostsPercent).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}</td>
                                    <td className="numeric">{formatMoney(row.administrativeCosts)}</td>
                                    <td><span className={`tourist-event ${row.isTouristEvent ? "yes" : "no"}`}>{row.isTouristEvent ? "Tak" : "Nie"}</span></td>
                                    <td className="notes">{row.notes || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan="2">Razem</td>
                                <td className="numeric">{formatMoney(totals.income)}</td>
                                <td className="numeric">{formatMoney(totals.costs)}</td>
                                <td className={`numeric balance ${totals.balance < 0 ? "negative" : "positive"}`}>{formatMoney(totals.balance)}</td>
                                <td className={`numeric balance ${totals.balance < 0 ? "negative" : "positive"}`}>{totals.costs === 0 ? "—" : `${(totals.balance / totals.costs * 100).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}</td>
                                <td className="numeric">{formatMoney(totals.administrativeCosts)}</td>
                                <td colSpan="2"></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
};

export default TripBalanceView;
